import { Spin } from 'antd'
import dayjs from 'dayjs'
import { lazy, Suspense } from 'react'
import { AiOutlineCalendar, AiOutlineInteraction } from 'react-icons/ai'

import { StatusDictionarySelect } from '@/Ship/Components/FormFields/StatusDictionarySelect'
import { defineView, renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { ActivityOwnerLink } from '../Components/ActivityOwnerLink'
import {
    ActivityDeadline,
    ActivityDescription,
    ActivityResponsible,
    ActivityStatusTags,
    activityRowClassName,
} from '../Components/ActivityCells'
import { ActivityRegistryHints } from '../Components/ActivityRegistryHints'
import {
    ACTIVITY_DRAWER,
    ACTIVITY_REST_URL,
    ACTIVITY_STATUS_RESOURCE,
    ACTIVITY_TYPE_RESOURCE,
    OWNER_FIELDS,
    OWNER_RESOURCES,
} from '../Contracts/Activity.types'
import { ACTIVITY_FIELD_LABELS as L, ACTIVITY_TEXT_MAX_LENGTH } from '../Contracts/ActivityFieldLabels'
import { filterContactsOfOwner, ownerKindOf } from '../Helpers/ownerContact'
import { ActivityViewButtons } from '../Hooks/ActivityViewActions'
import { ActivityFormTemplate } from '../Templates/ActivityFormTemplate'

import type { ActivityCalendarProps } from '../Components/ActivityCalendar'
import type { ActivityOwnerKind, ActivityRecord, ActivityRelationRef } from '../Contracts/Activity.types'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { ReactNode } from 'react'

// Lazy: календар (FullCalendar) не потрібен, поки його не відкрили
const ActivityCalendar = lazy(() => import('../Components/ActivityCalendar'))

const LazyCalendarTemplate = (props: ActivityCalendarProps) => (
    <Suspense
        fallback={
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spin />
            </div>
        }
    >
        <ActivityCalendar {...props} />
    </Suspense>
)

/** Рядок таблиці: ядро кладе сирий запис у `_original`, а поля перетворює для показу. */
const sourceOf = (row: unknown): ActivityRecord => {
    const item = (row ?? {}) as ActivityRecord & { _original?: ActivityRecord }

    return item._original ?? item
}

/** Лише перегляд: ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Власник у перегляді — посиланням на його картку (К-8); у редагуванні —
 * селект його ресурсу (лише видимі користувачу клієнти — правило рядка
 * клієнтів, К-7).
 */
const ownerField = (kind: ActivityOwnerKind): IFieldConfig => ({
    label: L[kind],
    type: 'select',
    resource: OWNER_RESOURCES[kind].owner,
    view: defineView((value) =>
        value !== null && typeof value === 'object' ? (
            <ActivityOwnerLink kind={kind} owner={value as ActivityRelationRef} />
        ) : (
            '—'
        )
    ),
})

/** Контакт — лише обраного власника (К-9, `filterContactsOfOwner`). */
const contactField = (kind: ActivityOwnerKind): IFieldConfig => ({
    label: L[OWNER_FIELDS[kind].contact],
    type: 'select',
    resource: OWNER_RESOURCES[kind].contact,
    depFn: filterContactsOfOwner(kind),
})

/**
 * Поля, яких бек від форми не приймає (`SPEC-011` §3.5): статус, завершення,
 * службове й обчислюване ставить він сам; `ownerType` і `ownerFixed` живуть
 * лише у формі (`SPEC-011` §3.3). `externalCode` пише лише адміністратор і
 * не з форми — тіло з ним без права дало б 403.
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'status',
    'isOverdue',
    'doneBy',
    'doneAt',
    'isRemoved',
    'externalCode',
    'ownerType',
    'ownerFixed',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

/**
 * Тіло запиту картки перед відправкою: без полів лише для читання, і рівно
 * один власник (І4). Пара іншого власника йде явним `null`: PATCH справи,
 * яку перемкнули з клієнта на перевізника, мусить зняти клієнта, а не лишити
 * його мовчки.
 */
const prepareActivityPayload = ({ values }: { values: Record<string, unknown> }): void => {
    const kind = ownerKindOf(values as ActivityRecord)
    const other = OWNER_FIELDS[kind === 'client' ? 'carrier' : 'client']

    READ_ONLY_KEYS.forEach((key) => delete values[key])

    values[other.owner] = null
    values[other.contact] = null
}

/**
 * Колонки, які ядро само не намалює як треба (`SPEC-011` §8.1):
 *
 *  - **«Клієнт|Перевізник»** (Р7) — назва власника посиланням на його
 *    картку; другою колонкою, як у легасі (`ActivityCrud.jsx:117-126`);
 *  - **«Опис»** — одним рядком із трикрапкою (Р8);
 *  - **«Крайній термін»** — «дд.мм.рррр, гг:хх», сортування (Р9);
 *  - **«Відповідальний»** — коротке ім'я (Р10);
 *  - **«Статус»** — бейдж кольору довідника й «Прострочена» (Р11).
 */
const AddActivityColumnRenderers = ({ columns }: { columns: Array<Record<string, unknown>> }) => {
    if (!columns.some((column) => column['key'] === 'owner')) {
        const typeIndex = columns.findIndex((column) => column['key'] === 'type')

        columns.splice(typeIndex + 1, 0, {
            title: L.owner,
            key: 'owner',
            dataIndex: 'owner',
            render: (_: unknown, row: unknown): ReactNode => {
                const record = sourceOf(row)
                const kind: ActivityOwnerKind | null = record.client ? 'client' : record.carrier ? 'carrier' : null
                const owner = kind ? record[kind] : null

                return kind && owner ? <ActivityOwnerLink kind={kind} owner={owner} /> : '—'
            },
        })
    }

    columns.forEach((column) => {
        switch (column['key']) {
            case 'type':
                column['render'] = (_: unknown, row: unknown): ReactNode => sourceOf(row).type?.title || '—'
                break
            case 'description':
                column['render'] = (_: unknown, row: unknown): ReactNode => (
                    <ActivityDescription text={sourceOf(row).description} />
                )
                break
            case 'deadline':
                column['sorter'] = true
                column['render'] = (_: unknown, row: unknown): ReactNode => (
                    <ActivityDeadline value={sourceOf(row).deadline} />
                )
                break
            case 'responsible':
                column['render'] = (_: unknown, row: unknown): ReactNode => <ActivityResponsible record={sourceOf(row)} />
                break
            case 'status':
                column['render'] = (_: unknown, row: unknown): ReactNode => <ActivityStatusTags record={sourceOf(row)} />
                break
        }
    })

    return columns
}

/** Мінімум headerPage-контексту, потрібний режиму «Календар». */
interface ActivityHeaderPageContextLike {
    storages?: {
        crudContext?: ActivityCalendarProps['crudContext'] & {
            listContext?: { variantItems?: Record<string, unknown> }
        }
    }
}

/**
 * Режим «Календар» поруч із таблицею (`SPEC-011` §8.1, Р4) — без змін ядра:
 * `resource.headerPage` мержиться у headerPage-контекст, і обробник дописує
 * варіант у `variantItems` синхронно, у тому ж ланцюгу, що й ядровий
 * табличний режим (`variantItems` у ядрі не observable). Перемикач малює
 * ядровий `PushModeListToExtra`.
 */
const AddActivityCalendarMode = ({ headerPageContext }: { headerPageContext?: ActivityHeaderPageContextLike }): void => {
    const crudContext = headerPageContext?.storages?.crudContext
    const listContext = crudContext?.listContext

    if (!crudContext || !listContext) return

    listContext.variantItems = {
        ...(listContext.variantItems ?? {}),
        calendar: {
            label: 'Календар',
            icon: AiOutlineCalendar,
            template: LazyCalendarTemplate,
            props: { crudContext },
        },
    }
}

/** Кнопки фільтра «Крайній термін» понад ядрові пресети (`SPEC-011` §8.2, Ф5а–Ф5б). */
type RangeChange = (value: [dayjs.Dayjs, dayjs.Dayjs]) => void

const DEADLINE_BUTTONS = [
    {
        // Від початку доби пів року тому до «зараз» (легасі `ActivityCrud.jsx:66-77`)
        label: 'Минулий період',
        key: 'past-period',
        onClick: ({ onChange }: { onChange: RangeChange }) =>
            onChange([dayjs().subtract(6, 'month').startOf('day'), dayjs()]),
    },
    {
        // У ядровому переліку «Завтра» немає (`DatePickerRangeInput`)
        label: 'Завтра',
        key: 'tomorrow',
        onClick: ({ onChange }: { onChange: RangeChange }) =>
            onChange([dayjs().add(1, 'day').startOf('day'), dayjs().add(1, 'day').endOf('day')]),
    },
]

/**
 * Справи (`SPEC-011` §8): реєстр, картка, календар.
 *
 * Права — лише з беку (`SPEC-003` §1.3, `SPEC-011` §10): «Нова справа» —
 * колекційне `create` (`ResourceCorrespondenceConfig`), «Редагувати» /
 * «Видалити» / «Завершити справу» — `_permissions` запису. Список і картку бек
 * звужує сам: відповідальний, автор або адміністратор.
 *
 * Над таблицею — рядок статусів довідника «Статуси справ» з лічильниками
 * `GET /services/activities/counters` (`StatusFilterBar`, Р5); червоний рядок —
 * предикат П (Р12). Пошуку немає (Р16).
 */
export const ActivityResource: IResourceConfig = {
    title: 'Справи',
    fields: {
        id: readOnly({
            label: L.id,
            type: 'number',
        }),
        type: {
            label: L.type,
            type: 'select',
            resource: ACTIVITY_TYPE_RESOURCE,
            isRequired: true,
        },
        description: {
            label: L.description,
            type: 'textarea',
            inputProps: { maxLength: ACTIVITY_TEXT_MAX_LENGTH },
        },
        client: ownerField('client'),
        clientContact: contactField('client'),
        carrier: ownerField('carrier'),
        carrierContact: contactField('carrier'),
        responsible: {
            label: L.responsible,
            type: 'select',
            // Список співробітників ядро віддає лише з активними; бек ще й
            // відхиляє неактивного (І9)
            resource: 'Setting.User',
            isRequired: true,
            view: renderUsersView,
        },
        deadline: {
            label: L.deadline,
            type: 'datetime',
            isRequired: true,
            view: renderDateTimeView,
            // «Минулий період» і «Завтра» над ядровими пресетами діапазону:
            // їх читає лише поле фільтра (`DatePickerRangeInput` бере
            // `field.props.buttons`, а пропси `fieldProps` до поля не доходять)
            props: { buttons: DEADLINE_BUTTONS },
        },
        // Статус ставлять створення («Нова») і «Завершити справу» — у формі
        // лише перегляд. `inputProps.disabled` вимикає й ядрову колонку, яка
        // інакше слала б `PATCH {status}` (вартовий колонки статусу)
        status: {
            label: L.status,
            type: 'status',
            statusResource: ACTIVITY_STATUS_RESOURCE,
            isEditable: false,
            inputProps: { disabled: true },
        },
        result: {
            label: L.result,
            type: 'textarea',
            inputProps: { maxLength: ACTIVITY_TEXT_MAX_LENGTH },
        },
        doneBy: readOnly({
            label: L.doneBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        doneAt: readOnly({
            label: L.doneAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    // Колонки й порядок легасі (`ActivityCrud.jsx:82`); «Клієнт|Перевізник»
    // додає `AddActivityColumnRenderers` другою, «Ід» вмикає шестерня
    table: {
        showColumns: ['type', 'description', 'deadline', 'responsible', 'status'],
        dataSourceNoTransform: ['status'],
        rowClassName: (row: unknown) => activityRowClassName(sourceOf(row)),
        eventHandlers: {
            onBuildColumns: { AddActivityColumnRenderers },
        },
    },
    // Фільтри в порядку легасі (`ActivityCrud.jsx:64`)
    filter: {
        includeFields: ['id', 'client', 'carrier', 'status', 'deadline', 'responsible'],
        fieldProps: {
            // Варіанти — з того самого довідника, що й рядок статусів; значення — код
            status: { input: StatusDictionarySelect },
        },
    },
    headerPage: {
        eventHandlers: {
            onAfterInit: { AddActivityCalendarMode },
        },
    },
    // Пояснення над реєстром: червоний рядок і чиї справи в списку (`SPEC-011` §11)
    places: {
        top: <ActivityRegistryHints />,
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                ActivityPayload: prepareActivityPayload,
            },
        },
    },
    detailConfig: {
        title: 'Справа',
        modeConfig: {
            // «Завершити справу» — лише в перегляді й лише за `_permissions`
            view: { template: ActivityFormTemplate, onButtons: ActivityViewButtons },
            edit: { template: ActivityFormTemplate },
            create: { template: ActivityFormTemplate },
        },
    },
    // Блоки полями по два: шухляди легасі на 600 px для цього тісно; заголовки
    // «Справа» / «Нова справа» (К-1)
    drawer: { ...ACTIVITY_DRAWER },
    restUrl: ACTIVITY_REST_URL,
    pageUrl: '/activities',
    pageConfig: {
        title: 'Справи',
        icon: <AiOutlineInteraction />,
    },
    settings: {
        addButtonLabel: 'Нова справа',
    },
}
